import { c as _c } from "react/compiler-runtime";
import { motion } from 'framer-motion';
import { useTranslations } from 'next-intl';
import { FC, SVGProps } from 'react';
import { useFormContext } from 'react-hook-form';
import ViverseInput from '@/components/ViverseInput';
import ViverseSwitch from '@/components/ViverseSwitch';
import ViverseTextarea from '@/components/ViverseTextarea';
import IconX from '@/components/assets/icon_X.svg';
import IconBluesky from '@/components/assets/icon_bluesky.svg';
import IconDiscord from '@/components/assets/icon_discord.svg';
import IconFacebook from '@/components/assets/icon_facebook.svg';
import IconInstagram from '@/components/assets/icon_instagram.svg';
import IconYoutube from '@/components/assets/icon_youtube.svg';
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Separator } from '@/components/ui/separator';
import { FormSchema, MAX_BIO_LENGTH, socialLinksSchema } from '@/schemas/media';
type SocialLinks = {
  Icon: FC<SVGProps<SVGElement>>;
  placeholder: string;
  name: keyof typeof socialLinksSchema.shape;
};
export const socialLinksMap: Record<keyof typeof socialLinksSchema.shape, SocialLinks> = {
  youtube: {
    Icon: IconYoutube,
    placeholder: 'https://www.youtube.com/',
    name: 'youtube'
  },
  x: {
    Icon: IconX,
    placeholder: 'https://x.com/',
    name: 'x'
  },
  discord: {
    Icon: IconDiscord,
    placeholder: 'https://discord.com/',
    name: 'discord'
  },
  facebook: {
    Icon: IconFacebook,
    placeholder: 'https://www.facebook.com/',
    name: 'facebook'
  },
  instagram: {
    Icon: IconInstagram,
    placeholder: 'https://www.instagram.com/',
    name: 'instagram'
  },
  bluesky: {
    Icon: IconBluesky,
    placeholder: 'https://bsky.app/',
    name: 'bluesky'
  }
};
function EditUserAbout() {
  const $ = _c(42);
  const t = useTranslations();
  const form = useFormContext();
  let t0;
  let t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      opacity: 0,
      y: -5
    };
    t1 = {
      opacity: 1,
      y: 0
    };
    t2 = {
      opacity: 0,
      y: 5
    };
    $[0] = t0;
    $[1] = t1;
    $[2] = t2;
  } else {
    t0 = $[0];
    t1 = $[1];
    t2 = $[2];
  }
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <Separator className="bg-vive-primary-60 h-0.5 w-full" />;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== t) {
    t4 = t5 => {
      const {
        field
      } = t5;
      return <FormItem><FormLabel><p className="header-5 mb-2">{t("profile-page.about.bio")}</p></FormLabel><FormControl><ViverseTextarea placeholder={t("profile-page.about.bio-edit-placeholder")} className="bg-vive-primary-70 placeholder-vive-primary-30 px-5 py-3 tracking-[0.2px]" rows={5} maxLength={MAX_BIO_LENGTH} showMaxLength={false} {...field} /></FormControl><FormMessage /></FormItem>;
    };
    $[4] = t;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== form.control || $[7] !== t4) {
    t5 = <FormField control={form.control} name="bio" render={t4} />;
    $[6] = form.control;
    $[7] = t4;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== form) {
    t6 = form.watch("bio")?.length || 0;
    $[9] = form;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  const t7 = MAX_BIO_LENGTH - t6;
  let t8;
  if ($[11] !== t || $[12] !== t7) {
    t8 = t("profile-page.about.bio-max-length", {
      count: t7
    });
    $[11] = t;
    $[12] = t7;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] !== t8) {
    t9 = <p className="caption text-right">{t8}</p>;
    $[14] = t8;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  let t10;
  if ($[16] !== t5 || $[17] !== t9) {
    t10 = <section className="flex flex-col gap-5"><div className="flex flex-col gap-2 lg:max-w-1/2">{t5}{t9}</div></section>;
    $[16] = t5;
    $[17] = t9;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  let t11;
  if ($[19] !== t) {
    t11 = t("profile-page.about.social-accounts");
    $[19] = t;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  let t12;
  if ($[21] !== t11) {
    t12 = <h2 className="header-5">{t11}</h2>;
    $[21] = t11;
    $[22] = t12;
  } else {
    t12 = $[22];
  }
  let t13;
  if ($[23] !== t) {
    t13 = t("profile-page.about.social-accounts-description");
    $[23] = t;
    $[24] = t13;
  } else {
    t13 = $[24];
  }
  let t14;
  if ($[25] !== t13) {
    t14 = <p className="body-1">{t13}</p>;
    $[25] = t13;
    $[26] = t14;
  } else {
    t14 = $[26];
  }
  let t15;
  if ($[27] !== form.control) {
    t15 = <FormField control={form.control} name="enableSocialLinks" render={_temp} />;
    $[27] = form.control;
    $[28] = t15;
  } else {
    t15 = $[28];
  }
  let t16;
  if ($[29] !== t14 || $[30] !== t15) {
    t16 = <div className="flex gap-3">{t14}{t15}</div>;
    $[29] = t14;
    $[30] = t15;
    $[31] = t16;
  } else {
    t16 = $[31];
  }
  let t17;
  if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = Object.values(socialLinksMap);
    $[32] = t17;
  } else {
    t17 = $[32];
  }
  let t18;
  if ($[33] !== form.control) {
    t18 = <div className="grid grid-cols-[320px] gap-8 md:grid-cols-2 md:gap-12">{t17.map((t19, index) => {
        const {
          Icon,
          placeholder,
          name
        } = t19;
        return <FormField key={index} control={form.control} name={`socialLinks.${name}`} render={t20 => {
          const {
            field: field_1
          } = t20;
          return <FormItem><FormControl><div className="flex items-center gap-2.5"><Icon className="size-7" /><ViverseInput className="bg-vive-primary-70 w-full px-5 py-3" placeholder={placeholder} {...field_1} /></div></FormControl><FormMessage className="text-vive-error ml-10" /></FormItem>;
        }} />;
      })}</div>;
    $[33] = form.control;
    $[34] = t18;
  } else {
    t18 = $[34];
  }
  let t19;
  if ($[35] !== t12 || $[36] !== t16 || $[37] !== t18) {
    t19 = <section className="flex flex-col gap-5 lg:max-w-2/3">{t12}{t16}{t18}</section>;
    $[35] = t12;
    $[36] = t16;
    $[37] = t18;
    $[38] = t19;
  } else {
    t19 = $[38];
  }
  let t20;
  if ($[39] !== t10 || $[40] !== t19) {
    t20 = <motion.div initial={t0} animate={t1} exit={t2} className="flex flex-col gap-5">{t3}{t10}{t19}</motion.div>;
    $[39] = t10;
    $[40] = t19;
    $[41] = t20;
  } else {
    t20 = $[41];
  }
  return t20;
}
function _temp(t0) {
  const {
    field: field_0
  } = t0;
  return <FormItem><FormControl><ViverseSwitch checked={field_0.value} onCheckedChange={field_0.onChange} /></FormControl></FormItem>;
}
export default EditUserAbout;